import { useParams, Link } from 'react-router-dom'
import { blogPosts } from '@/data/blogPosts'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Calendar, User, MapPin } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

const BlogPost = () => {
  const { slug } = useParams()
  const post = blogPosts.find((p) => p.slug === slug)

  useEffect(() => {
    window.scrollTo(0, 0)

    if (post) {
      // Set Document Title
      document.title = `${post.title} | Blog SuperFut Kids`

      // Set Meta Description
      let metaDesc = document.querySelector('meta[name="description"]')
      if (!metaDesc) {
        metaDesc = document.createElement('meta')
        metaDesc.setAttribute('name', 'description')
        document.head.appendChild(metaDesc)
      }
      metaDesc.setAttribute('content', post.metaDescription)

      // Add Schema.org JSON-LD
      const scriptId = 'blog-post-schema'
      // Remove existing script if any
      const existingScript = document.getElementById(scriptId)
      if (existingScript) {
        existingScript.remove()
      }

      const script = document.createElement('script')
      script.id = scriptId
      script.type = 'application/ld+json'

      const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.metaDescription,
        image: post.image,
        author: {
          '@type': 'Organization',
          name: post.author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'SuperFut Kids',
          logo: {
            '@type': 'ImageObject',
            url: 'https://superfutkids.com.br/logo.png', // Placeholder URL
          },
        },
        datePublished: post.date, // Note: In a real app, use ISO date format
      }

      const schemas = [articleSchema]

      if (post.faq) {
        const faqSchema = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
        // @ts-expect-error - merging schemas roughly
        schemas.push(faqSchema)
      }

      script.textContent = JSON.stringify(schemas)
      document.head.appendChild(script)
    }

    return () => {
      // Cleanup schemas
      const script = document.getElementById('blog-post-schema')
      if (script) script.remove()
    }
  }, [post])

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          Artigo não encontrado
        </h1>
        <p className="text-gray-600 mb-8">
          O artigo que você está procurando não existe ou foi removido.
        </p>
        <Button asChild>
          <Link to="/blog">Voltar para o Blog</Link>
        </Button>
      </div>
    )
  }

  return (
    <article className="pt-24 pb-20 bg-white min-h-screen">
      {/* Header */}
      <div className="container px-4 md:px-6 mx-auto max-w-4xl">
        <div className="mb-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-super-blue transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Voltar para o blog
          </Link>

          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className="bg-super-blue hover:bg-sky-600">
              {post.category}
            </Badge>
          </div>

          <h1 className="text-3xl md:text-5xl font-display font-bold text-super-text leading-tight mb-6">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500 border-b border-slate-100 pb-8 mb-8">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="font-medium text-slate-700">{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{post.location}</span>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="rounded-2xl overflow-hidden shadow-lg mb-10">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-auto object-cover max-h-[500px]"
          />
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-slate max-w-none mb-12">
          {post.content}
        </div>

        {/* FAQ Section (Rendered if exists) */}
        {post.faq && (
          <div className="bg-slate-50 rounded-2xl p-8 mb-12 border border-slate-100">
            <h3 className="text-2xl font-bold text-super-text mb-6">
              {post.faqTitle || 'Perguntas Frequentes'}
            </h3>
            <Accordion type="single" collapsible className="w-full">
              {post.faq.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-b-slate-200"
                >
                  <AccordionTrigger className="text-slate-800 font-semibold hover:text-super-blue hover:no-underline text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Content After FAQ (if exists) */}
        {post.contentAfterFaq && (
          <div className="prose prose-lg prose-slate max-w-none mb-12">
            {post.contentAfterFaq}
          </div>
        )}

        {/* Bottom Navigation */}
        <div className="flex justify-center border-t border-slate-100 pt-8">
          <Button variant="outline" size="lg" className="rounded-full" asChild>
            <Link to="/blog">Ver mais artigos</Link>
          </Button>
        </div>
      </div>
    </article>
  )
}

export default BlogPost
